"use client";

import { useState, useEffect, useRef } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import * as XLSX from "xlsx";

export default function ChangeOrdersTab({ project, setProject }) {
  const navigate = useNavigate();
  const [changeOrders, setChangeOrders] = useState([]);
  const [error, setError] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [activeTab, setActiveTab] = useState("billable");
  const [sortField, setSortField] = useState("createdAt");
  const [searchQuery, setSearchQuery] = useState("");
  const [sortOrder, setSortOrder] = useState("desc");
  const [statusFilter, setStatusFilter] = useState("all");
  const [isStatusDropdownOpen, setIsStatusDropdownOpen] = useState(false);
  const dropdownRef = useRef(null);
  const [tandmCostMap, setTandmCostMap] = useState({});
  // Then update the useEffect where you fetch change orders:
  useEffect(() => {
    const fetchChangeOrders = async () => {
      setIsLoading(true);

      if (project.version === 2) {
        try {
          const token = localStorage.getItem("token");
          const response = await axios.get(
            `https://erp-production-72da01c8e651.herokuapp.com/api/changeOrders2/project/${project._id}`,
            { headers: { Authorization: `Bearer ${token}` } },
          );

          // Recalculate totals for each change order to ensure consistency
          const updatedOrders = response.data.changeOrders.map((co) => {
            const recalculatedTotals = calculateChangeOrderTotalsWithTandM(
              co,
              null,
            );

            return {
              ...co,
              createdAt: co.createdAt || "2025-09-25T00:00:00.000Z",
              // Override with recalculated values
              totalBudgetCost: recalculatedTotals.budgetCost,
              totalCustomerPrice: recalculatedTotals.totalCustomerPrice,
              // Store additional calculated values if needed
              _calculated: recalculatedTotals,
            };
          });

          setChangeOrders(updatedOrders);
        } catch (err) {
          setError(
            "Failed to load change order: " +
              (err.response?.data?.message || err.message),
          );
        } finally {
          setIsLoading(false);
        }
      } else {
        // ... existing v1 logic
      }
    };
    fetchChangeOrders();
  }, [project._id, project.version]);

  useEffect(() => {
    const fetchTandMCosts = async () => {
      const token = localStorage.getItem("token");
      const tandmCOs = changeOrders.filter(
        (co) => co.isTandMBased && co.linkedTandMId,
      );
      if (tandmCOs.length === 0) return;

      // Dedupe — multiple COs could share the same T&M
      const unique = new Map();
      tandmCOs.forEach((co) => {
        const id =
          co.linkedTandMId?._id?.toString() || co.linkedTandMId?.toString();
        if (id) unique.set(id, co);
      });

      const results = await Promise.allSettled(
        Array.from(unique.entries()).map(([tandmId, co]) =>
          axios
            .get(
              `https://erp-production-72da01c8e651.herokuapp.com/api/time-and-material2/${project._id}/${tandmId}/co-cost-preview`,
              { headers: { Authorization: `Bearer ${token}` } },
            )
            .then((res) => ({ tandmId, data: res.data })),
        ),
      );

      const newMap = {};
      results.forEach((r) => {
        if (r.status === "fulfilled") {
          newMap[r.value.tandmId] = r.value.data;
        }
      });
      setTandmCostMap(newMap);

      // Re-run recalculation with fresh T&M data
      setChangeOrders((prev) =>
        prev.map((co) => {
          if (!co.isTandMBased || !co.linkedTandMId) return co;
          const tandmId =
            co.linkedTandMId?._id?.toString() || co.linkedTandMId?.toString();
          const tandmData = newMap[tandmId] || null;
          const recalculated = calculateChangeOrderTotalsWithTandM(
            co,
            tandmData,
          );
          return { ...co, _calculated: recalculated };
        }),
      );
    };

    fetchTandMCosts();
  }, [changeOrders.length]); // trigger when COs first load, not on every render

  const calculateChangeOrderTotalsWithTandM = (
    changeOrder,
    tandmData = null,
  ) => {
    const isTandMBased = !!changeOrder.isTandMBased;
    const isApproved = changeOrder.approvalStatus === "Approved";

    // Step 1: Calculate base budget cost (original CO items, excluding amendments)
    let baseBudgetCost = 0;

    if (isTandMBased) {
      const tandmBaseCost =
        tandmData?.totals?.totalCost ?? changeOrder.totalBudgetCost ?? 0;

      if (isApproved) {
        baseBudgetCost = tandmBaseCost;
      } else {
        baseBudgetCost = tandmBaseCost;
        (changeOrder.items || [])
          .filter((item) => item.forBudget && !item.sourceLineItemId)
          .forEach((item) => {
            const signedQty = item.deltaQty || item.quantity || 0;
            const qty = Math.abs(signedQty);
            const coverage = parseFloat(item.coveragePerUnit) || 1;
            const isCoverageItem =
              item.category === "M" &&
              (isSFUnit(item.uom) || isLFUnit(item.uom));
            const effectiveCost =
              item.category !== "M" && item.unitRate > 0
                ? item.unitRate
                : item.unitCost || 0;
            const costTotal = isCoverageItem
              ? qty * coverage * effectiveCost
              : qty * effectiveCost;
            baseBudgetCost += signedQty < 0 ? -costTotal : costTotal;
          });
      }
    } else {
      (changeOrder.items || []).forEach((item) => {
        if (!item.forBudget) return;
        const signedQty = item.deltaQty || item.quantity || 0;
        const qty = Math.abs(signedQty);
        const coverage = parseFloat(item.coveragePerUnit) || 1;
        const isCoverageItem =
          item.category === "M" && (isSFUnit(item.uom) || isLFUnit(item.uom));
        const effectiveCost =
          item.category !== "M" && item.unitRate > 0
            ? item.unitRate
            : item.unitCost || 0;
        const costTotal = isCoverageItem
          ? qty * coverage * effectiveCost
          : qty * effectiveCost;
        baseBudgetCost += signedQty < 0 ? -costTotal : costTotal;
      });
    }

    // Step 2: Calculate total amendment cost (can be positive or negative)
    // KEY FIX: Properly handle signed deltaQty values
    const approvedAmendmentCost = (changeOrder.amendments || [])
      .filter((a) => a.status === "Approved")
      .reduce((sum, amendment) => {
        return (
          sum +
          (amendment.items || []).reduce((itemSum, item) => {
            const signedQty = item.deltaQty || 0; // Keep the sign!
            const qty = Math.abs(signedQty);
            const coverage = parseFloat(item.coveragePerUnit) || 1;
            const isCov =
              item.category === "M" &&
              (isSFUnit(item.uom) || isLFUnit(item.uom));
            const baseCost =
              item.category !== "M" && item.unitRate > 0
                ? item.unitRate
                : item.unitCost || 0;
            const costTotal = isCov
              ? qty * coverage * baseCost
              : qty * baseCost;
            // Respect the sign - negative qty = cost reduction
            return itemSum + (signedQty < 0 ? -costTotal : costTotal);
          }, 0)
        );
      }, 0);

    // Step 3: Calculate final budget cost
    const finalBudgetCost =
      isTandMBased && isApproved
        ? baseBudgetCost
        : baseBudgetCost + approvedAmendmentCost;

    // Customer pricing calculation (unchanged)
    const sectionBreakdownTotals = calculateSectionBreakdownTotals(
      changeOrder.pricingSections || [],
      changeOrder.items || [],
      changeOrder.defaultMarkupPercentage,
    );

    let customerSubtotal = 0;
    let totalMaterialPrice = 0;
    let totalLaborPrice = 0;

    (changeOrder.pricingSections || []).forEach((section) => {
      const bd = sectionBreakdownTotals[section.number] || {
        materialTotal: 0,
        laborTotal: 0,
        materialItems: [],
        laborItems: [],
      };
      const hasBreakdown =
        (bd.materialItems?.length || 0) + (bd.laborItems?.length || 0) > 0;
      const sectionMaterial = hasBreakdown
        ? bd.materialTotal || 0
        : section.materialPrice || 0;
      const sectionLabor = hasBreakdown
        ? bd.laborTotal || 0
        : section.laborPrice || 0;
      totalMaterialPrice += sectionMaterial;
      totalLaborPrice += sectionLabor;
      customerSubtotal += sectionMaterial + sectionLabor;
    });

    const overheadAmount =
      customerSubtotal * ((changeOrder.overheadPercentage || 0) / 100);
    const totalCustomerPrice = customerSubtotal + overheadAmount;

    return {
      budgetCost: Number(finalBudgetCost.toFixed(2)), // Total including amendments
      amendmentCost: Number(approvedAmendmentCost.toFixed(2)), // Can be negative!
      baseBudgetCost: Number(baseBudgetCost.toFixed(2)), // Original before amendments
      customerSubtotal: Number(customerSubtotal.toFixed(2)),
      totalMaterialPrice: Number(totalMaterialPrice.toFixed(2)),
      totalLaborPrice: Number(totalLaborPrice.toFixed(2)),
      overheadAmount: Number(overheadAmount.toFixed(2)),
      totalCustomerPrice: Number(totalCustomerPrice.toFixed(2)),
      margin: Number((customerSubtotal - Math.abs(finalBudgetCost)).toFixed(2)),
      marginPercent:
        customerSubtotal > 0
          ? Number(
              (
                ((customerSubtotal - Math.abs(finalBudgetCost)) /
                  customerSubtotal) *
                100
              ).toFixed(1),
            )
          : 0,
    };
  };
  // Update the subtotal calculation to use recalculated values

  const calculateSectionBreakdownTotals = (
    pricingSections,
    changeItems,
    defaultMarkupPercentage,
  ) => {
    const totals = {};

    pricingSections.forEach((section) => {
      // Get breakdown items from budget items
      const breakdownItems = changeItems.filter(
        (i) => i.referenceNumber === section.number && i.includeInBreakdown,
      );

      let materialTotal = 0;
      let laborTotal = 0;
      const materialItems = [];
      const laborItems = [];

      // Process breakdown items from budget
      breakdownItems.forEach((item) => {
        const displayQty =
          item.breakdownQty ?? Math.abs(item.deltaQty || item.quantity || 0);

        const unitPrice =
          item._breakdownUnitPriceManuallySet &&
          item.breakdownUnitPrice !== undefined
            ? item.breakdownUnitPrice
            : calculateBreakdownUnitPrice(item, defaultMarkupPercentage);

        const lineTotal = displayQty * unitPrice;
        const breakdownUnit = item.breakdownUnit || item.uom || "EA";

        const breakdownItem = {
          ...item,
          breakdownQty: Number(displayQty.toFixed(2)),
          breakdownUnitPrice: Number(unitPrice.toFixed(2)),
          breakdownTotal: Number(lineTotal.toFixed(2)),
          breakdownUnit,
          breakdownDescription:
            item.breakdownDescription ||
            item.customerDescription ||
            item.itemDescription ||
            "",
          isCustom: false,
        };

        if (item.category === "M") {
          materialTotal += lineTotal;
          materialItems.push(breakdownItem);
        } else {
          laborTotal += lineTotal;
          laborItems.push(breakdownItem);
        }
      });

      // Process custom items from pricing section
      (section.customItems || []).forEach((customItem) => {
        const lineTotal = (customItem.qty || 0) * (customItem.unitPrice || 0);

        const customBreakdownItem = {
          _id: customItem._id,
          _localId: `custom-${section.number}-${customItem._id}`,
          breakdownQty: customItem.qty || 0,
          breakdownUnitPrice: customItem.unitPrice || 0,
          breakdownTotal: Number(lineTotal.toFixed(2)),
          breakdownUnit: customItem.unit || "EA",
          breakdownDescription: customItem.description || "",
          category: customItem.category || "M",
          isCustom: true,
        };

        if (customItem.category === "M") {
          materialTotal += lineTotal;
          materialItems.push(customBreakdownItem);
        } else {
          laborTotal += lineTotal;
          laborItems.push(customBreakdownItem);
        }
      });

      totals[section.number] = {
        materialTotal: Number(materialTotal.toFixed(2)),
        laborTotal: Number(laborTotal.toFixed(2)),
        materialItems,
        laborItems,
      };
    });

    return totals;
  };

  const calculateChangeOrderTotals = (changeOrder) => {
    // For approved T&M-based COs the correct merged cost is already persisted
    // on the document — trust it rather than re-deriving from items, which
    // would miss the T&M base cost entirely.
    const isTandMBased = !!changeOrder.isTandMBased;
    const isApproved = changeOrder.approvalStatus === "Approved";

    let budgetCost = 0;

    if (isTandMBased && isApproved) {
      // Server already stored the correct merged total on approval
      budgetCost = changeOrder.totalBudgetCost || 0;
    } else if (isTandMBased && !isApproved) {
      // Pending T&M CO: use the stored T&M base cost from the linked T&M snapshot
      // (savedTandMCost is persisted by the server when the CO is saved)
      budgetCost =
        changeOrder.savedTandMCost || changeOrder.totalBudgetCost || 0;
      // Add any extra items the PM added directly
      (changeOrder.items || [])
        .filter((i) => !i.sourceLineItemId)
        .forEach((item) => {
          if (!item.forBudget) return;
          const signedQty = item.deltaQty || item.quantity || 0;
          const qty = Math.abs(signedQty);
          const coverage = parseFloat(item.coveragePerUnit) || 1;
          const isCoverageItem =
            item.category === "M" && (isSFUnit(item.uom) || isLFUnit(item.uom));
          const effectiveCost =
            item.category !== "M" && item.unitRate > 0
              ? item.unitRate
              : item.unitCost || 0;
          const costTotal = isCoverageItem
            ? qty * coverage * effectiveCost
            : qty * effectiveCost;
          budgetCost += signedQty < 0 ? -costTotal : costTotal;
        });
    } else {
      // Standard (non-T&M) CO — recalculate from items as before
      (changeOrder.items || []).forEach((item) => {
        if (!item.forBudget) return;
        const signedQty = item.deltaQty || item.quantity || 0;
        const qty = Math.abs(signedQty);
        const coverage = parseFloat(item.coveragePerUnit) || 1;
        const isCoverageItem =
          item.category === "M" && (isSFUnit(item.uom) || isLFUnit(item.uom));
        const effectiveCost =
          item.category !== "M" && item.unitRate > 0
            ? item.unitRate
            : item.unitCost || 0;
        const costTotal = isCoverageItem
          ? qty * coverage * effectiveCost
          : qty * effectiveCost;
        budgetCost += signedQty < 0 ? -costTotal : costTotal;
      });
    }

    // Customer pricing — unchanged for all CO types
    const sectionBreakdownTotals = calculateSectionBreakdownTotals(
      changeOrder.pricingSections || [],
      changeOrder.items || [],
      changeOrder.defaultMarkupPercentage,
    );

    let customerSubtotal = 0;
    let totalMaterialPrice = 0;
    let totalLaborPrice = 0;

    (changeOrder.pricingSections || []).forEach((section) => {
      const bd = sectionBreakdownTotals[section.number] || {
        materialTotal: 0,
        laborTotal: 0,
        materialItems: [],
        laborItems: [],
      };
      const hasBreakdownOrCustom =
        (bd.materialItems?.length || 0) + (bd.laborItems?.length || 0) > 0;
      const sectionMaterial = hasBreakdownOrCustom
        ? bd.materialTotal || 0
        : section.materialPrice || 0;
      const sectionLabor = hasBreakdownOrCustom
        ? bd.laborTotal || 0
        : section.laborPrice || 0;
      totalMaterialPrice += sectionMaterial;
      totalLaborPrice += sectionLabor;
      customerSubtotal += sectionMaterial + sectionLabor;
    });

    const overheadAmount =
      customerSubtotal * ((changeOrder.overheadPercentage || 0) / 100);
    const totalCustomerPrice = customerSubtotal + overheadAmount;

    const approvedAmendmentCost = (changeOrder.amendments || [])
      .filter((a) => a.status === "Approved")
      .reduce((sum, amendment) => {
        return (
          sum +
          (amendment.items || []).reduce((s, item) => {
            const qty = Math.abs(item.deltaQty || 0);
            const coverage = parseFloat(item.coveragePerUnit) || 1;
            const isCov =
              item.category === "M" &&
              (isSFUnit(item.uom) || isLFUnit(item.uom));
            const baseCost =
              item.category !== "M" && item.unitRate > 0
                ? item.unitRate
                : item.unitCost || 0;
            const cost = isCov ? qty * coverage * baseCost : qty * baseCost;
            return s + ((item.deltaQty || 0) < 0 ? -cost : cost);
          }, 0)
        );
      }, 0);

    // For approved T&M COs don't double-add amendments — they're already in
    // totalBudgetCost if the server merged them.
    const finalBudgetCost =
      isTandMBased && isApproved
        ? budgetCost
        : budgetCost + approvedAmendmentCost;

    return {
      budgetCost: Number(finalBudgetCost.toFixed(2)),
      amendmentCost: Number(approvedAmendmentCost.toFixed(2)),
      baseBudgetCost: Number(budgetCost.toFixed(2)),
      customerSubtotal: Number(customerSubtotal.toFixed(2)),
      totalMaterialPrice: Number(totalMaterialPrice.toFixed(2)),
      totalLaborPrice: Number(totalLaborPrice.toFixed(2)),
      overheadAmount: Number(overheadAmount.toFixed(2)),
      totalCustomerPrice: Number(totalCustomerPrice.toFixed(2)),
      margin: Number((customerSubtotal - Math.abs(budgetCost)).toFixed(2)),
      marginPercent:
        customerSubtotal > 0
          ? Number(
              (
                ((customerSubtotal - Math.abs(budgetCost)) / customerSubtotal) *
                100
              ).toFixed(1),
            )
          : 0,
    };
  };

  const isSFUnit = (uom) => {
    const u = (uom || "").toLowerCase();
    return (
      u === "sf" || u.includes("square") || u.includes("sq ft") || u === "sheet"
    );
  };

  const isLFUnit = (uom) => {
    const u = (uom || "").toLowerCase();
    return u === "lf" || u.includes("linear") || u.includes("lin ft");
  };

  const calculateBreakdownUnitPrice = (item, defaultMarkupPercentage) => {
    const markupMultiplier = 1 + (defaultMarkupPercentage || 0) / 100;

    // Base cost per piece / per unit
    let baseCostPerPiece = 0;

    if (item.category === "M") {
      baseCostPerPiece = item.unitRate > 0 ? item.unitRate : item.unitCost || 0;
    } else {
      baseCostPerPiece = item.unitRate > 0 ? item.unitRate : item.unitCost || 0;
    }

    // For coverage items → multiply coverage into the per-piece selling price
    const isCoverageItem =
      item.category === "M" && (isSFUnit(item.uom) || isLFUnit(item.uom));
    const coverage = isCoverageItem ? parseFloat(item.coveragePerUnit) || 1 : 1;

    // Selling price PER PIECE = base cost per coverage unit × coverage × markup
    const sellingPricePerPiece = baseCostPerPiece * coverage * markupMultiplier;

    return Number(sellingPricePerPiece.toFixed(2));
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsStatusDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleGenerateChangeOrder = () => {
    if (project.version === 2) {
      navigate(`/projects/${project._id}/change-orders2/new`, {
        state: {
          budgetItems: project.budgets.flatMap((budget) =>
            (budget.lineItems || []).map((item) => ({
              ...item,
              budgetId: budget._id,
              budgetTitle: budget.title,
            })),
          ),
        },
      });
    } else {
      navigate(`/projects/${project._id}/change-orders/new`, {
        state: {
          budgetItems: project.budgets.flatMap((budget) =>
            (budget.lineItems || []).map((item) => ({
              ...item,
              budgetId: budget._id,
              budgetTitle: budget.title,
            })),
          ),
        },
      });
    }
  };

  const formatNumber = (num) =>
    num.toLocaleString("en-US", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });

  const formatDate = (date) =>
    new Date(date).toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
    });

  const handleSort = (field) => {
    if (field === sortField) {
      setSortOrder(sortOrder === "asc" ? "desc" : "asc");
    } else {
      setSortField(field);
      setSortOrder("asc");
    }
  };

  const handleStatusSelect = (status) => {
    setStatusFilter(status);
    setIsStatusDropdownOpen(false);
  };

  const handleClearFilter = () => {
    setStatusFilter("all");
    setIsStatusDropdownOpen(false);
  };

  const filteredChangeOrders = changeOrders
    .filter((co) =>
      activeTab === "billable" ? co.summary.billable : !co.summary.billable,
    )
    .filter((co) =>
      statusFilter === "all"
        ? true
        : co.approvalStatus.toLowerCase() === statusFilter.toLowerCase(),
    )
    .filter((co) => {
      if (!searchQuery.trim()) return true;
      const q = searchQuery.toLowerCase();
      return (
        (co.title || "").toLowerCase().includes(q) ||
        (co.referenceNumber || "").toLowerCase().includes(q) ||
        (co.changeOrderNumber || "").toString().toLowerCase().includes(q)
      );
    })
    .sort((a, b) => {
      const aValue =
        sortField === "createdAt" ? new Date(a[sortField]) : a[sortField];
      const bValue =
        sortField === "createdAt" ? new Date(b[sortField]) : b[sortField];

      if (aValue < bValue) return sortOrder === "asc" ? -1 : 1;
      if (aValue > bValue) return sortOrder === "asc" ? 1 : -1;
      return 0;
    });

  // REPLACE WITH:
  // REPLACE WITH:
  const subtotal2 = filteredChangeOrders.reduce(
    (acc, co) => {
      const cost = co._calculated?.budgetCost || co.totalBudgetCost || 0;
      const price =
        co._calculated?.totalCustomerPrice || co.totalCustomerPrice || 0;
      const isTandM = !!co.isTandMBased;

      // For T&M-based COs, get the real T&M cost from tandmCostMap if available
      const tandmId =
        co.linkedTandMId?._id?.toString() || co.linkedTandMId?.toString();
      const tandmData = tandmId ? tandmCostMap[tandmId] : null;
      const tandmLinkedCost =
        tandmData?.totals?.totalCost ?? (isTandM ? cost : 0);

      return {
        totalCost: acc.totalCost + (isTandM ? 0 : cost),
        totalPrice: acc.totalPrice + price,
        totalAmendments:
          acc.totalAmendments + (co._calculated?.amendmentCost || 0),
        tandmCost: acc.tandmCost + (isTandM ? tandmLinkedCost : 0),
      };
    },
    { totalCost: 0, totalPrice: 0, totalAmendments: 0, tandmCost: 0 },
  );
  const subtotal = filteredChangeOrders.reduce(
    (acc, co) => ({
      totalCost: acc.totalCost + co.totalCost,
      totalPrice: acc.totalPrice + co.totalPrice,
    }),
    { totalCost: 0, totalPrice: 0 },
  );

  const handleExportToExcel = () => {
    // Prepare data for Excel
    const data = filteredChangeOrders.map((co) => ({
      Title: co.title,
      "Change Order #": co.changeOrderNumber,
      "Total Cost": `$${formatNumber(co.totalCost)}`,
      "Total Price": `$${formatNumber(co.totalPrice)}`,
      Status:
        co.approvalStatus.charAt(0).toUpperCase() + co.approvalStatus.slice(1),
      Date: formatDate(co.createdAt),
    }));

    // Add subtotal row
    if (filteredChangeOrders.length > 0) {
      data.push({
        Title: "Subtotal",
        "Change Order #": "",
        "Total Cost": `$${formatNumber(subtotal.totalCost)}`,
        "Total Price": `$${formatNumber(subtotal.totalPrice)}`,
        Status: "",
        Date: "",
      });
    }

    // Create worksheet
    const ws = XLSX.utils.json_to_sheet(data);
    // Create workbook and add the worksheet
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(
      wb,
      ws,
      `${activeTab.charAt(0).toUpperCase() + activeTab.slice(1)} Change Orders`,
    );
    console.log(project);
    // Generate and download Excel file
    XLSX.writeFile(wb, `${project.projectName}_change_order_summary.xlsx`);
  };

  if (isLoading)
    return (
      <div className="text-center py-4 text-gray-200 bg-gray-700">
        Loading...
      </div>
    );
  if (error)
    return (
      <div className="text-red-400 text-center py-4 bg-gray-700">{error}</div>
    );

  return (
    <div className="bg-gray-700 p-6 rounded-lg shadow-md border border-gray-600">
      <div className="flex justify-between items-center mb-4">
        <h3 className="text-lg font-semibold text-white">Change Orders</h3>
        <div className="flex space-x-2">
          <button
            onClick={handleExportToExcel}
            className="px-4 py-2 text-sm text-white font-semibold bg-blue-500 rounded-md hover:bg-blue-600 transition-colors duration-200"
          >
            Export to Excel
          </button>
          <button
            onClick={handleGenerateChangeOrder}
            className="px-4 py-2 text-sm text-white font-semibold bg-orange-400 rounded-md hover:bg-orange-500 transition-colors duration-200"
          >
            Generate Change Order
          </button>
        </div>
      </div>

      <div className="flex items-center gap-3 mb-4">
        <button
          onClick={() => setActiveTab("billable")}
          className={`px-4 py-2 text-sm font-medium ${
            activeTab === "billable"
              ? "bg-orange-400 text-white"
              : "bg-gray-800 text-gray-400"
          } rounded-l-md hover:bg-orange-500 transition-colors duration-200`}
        >
          Billable
        </button>
        <button
          onClick={() => setActiveTab("non-billable")}
          className={`px-4 py-2 text-sm font-medium ${
            activeTab === "non-billable"
              ? "bg-orange-400 text-white"
              : "bg-gray-800 text-gray-400"
          } rounded-r-md hover:bg-orange-500 transition-colors duration-200`}
        >
          Non-Billable
        </button>

        <div className="relative flex-1 max-w-sm">
          <svg
            className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400 pointer-events-none"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M21 21l-4.35-4.35M17 11A6 6 0 1 1 5 11a6 6 0 0 1 12 0z"
            />
          </svg>
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Search title, ref #, CO #..."
            className="w-full pl-9 pr-8 py-2 text-sm bg-gray-800 border border-gray-600 rounded-md text-gray-200 placeholder-gray-500 focus:outline-none focus:border-orange-400 focus:ring-1 focus:ring-orange-400"
          />
          {searchQuery && (
            <button
              onClick={() => setSearchQuery("")}
              className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-200"
            >
              <svg
                className="h-4 w-4"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </button>
          )}
        </div>

        {searchQuery && (
          <span className="text-xs text-gray-400">
            {filteredChangeOrders.length} result
            {filteredChangeOrders.length !== 1 ? "s" : ""}
          </span>
        )}
      </div>

      <div className="overflow-hidden rounded-lg shadow-xl">
        <table className="min-w-full divide-y divide-gray-600 bg-gray-700">
          <thead className="bg-gray-800">
            <tr>
              <th
                className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider cursor-pointer"
                onClick={() => handleSort("referenceNumber")}
              >
                Customer Ref #{" "}
                {sortField === "referenceNumber" && (
                  <span className="text-orange-400">
                    {sortOrder === "asc" ? "↑" : "↓"}
                  </span>
                )}
              </th>
              <th
                className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider rounded-tl-lg cursor-pointer"
                onClick={() => handleSort("title")}
              >
                Title{" "}
                {sortField === "title" && (
                  <span className="text-orange-400">
                    {sortOrder === "asc" ? "↑" : "↓"}
                  </span>
                )}
              </th>
              <th
                className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider cursor-pointer"
                onClick={() => handleSort("changeOrderNumber")}
              >
                Change Order #{" "}
                {sortField === "changeOrderNumber" && (
                  <span className="text-orange-400">
                    {sortOrder === "asc" ? "↑" : "↓"}
                  </span>
                )}
              </th>
              <th
                className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider cursor-pointer"
                onClick={() => handleSort("totalCost")}
              >
                Total Cost{" "}
                {sortField === "totalCost" && (
                  <span className="text-orange-400">
                    {sortOrder === "asc" ? "↑" : "↓"}
                  </span>
                )}
              </th>
              <th
                className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider cursor-pointer"
                onClick={() => handleSort("totalPrice")}
              >
                Total Price{" "}
                {sortField === "totalPrice" && (
                  <span className="text-orange-400">
                    {sortOrder === "asc" ? "↑" : "↓"}
                  </span>
                )}
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider relative">
                <div
                  className="flex items-center cursor-pointer"
                  onClick={() => setIsStatusDropdownOpen(!isStatusDropdownOpen)}
                >
                  Status
                  {statusFilter !== "all" && (
                    <span className="ml-2 w-2 h-2 bg-orange-400 rounded-full"></span>
                  )}
                </div>
                {isStatusDropdownOpen && (
                  <div
                    ref={dropdownRef}
                    className="absolute z-10 mt-2 w-32 bg-gray-800 rounded-md shadow-lg"
                  >
                    <div className="py-1">
                      {["all", "Pending", "Approved", "Rejected"].map(
                        (status) => (
                          <button
                            key={status}
                            onClick={() => handleStatusSelect(status)}
                            className="block w-full text-left px-4 py-2 text-sm text-gray-200 hover:bg-gray-600"
                          >
                            {status === "all" ? "All" : status}
                          </button>
                        ),
                      )}
                    </div>
                  </div>
                )}
              </th>
              <th
                className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider cursor-pointer"
                onClick={() => handleSort("createdAt")}
              >
                Date{" "}
                {sortField === "createdAt" && (
                  <span className="text-orange-400">
                    {sortOrder === "asc" ? "↑" : "↓"}
                  </span>
                )}
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-600">
            {filteredChangeOrders.map((co) => (
              <tr
                key={co._id}
                onClick={(e) => {
                  if (e.target.closest(".actions-column")) return;
                  if (project.version === 2) {
                    navigate(
                      `/projects/${project._id}/change-orders2/${co._id}/edit`,
                    );
                  } else {
                    navigate(
                      `/projects/${project._id}/change-orders/${co._id}/edit`,
                    );
                  }
                }}
                className="hover:bg-gray-500 cursor-pointer transition-colors duration-200"
              >
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-400">
                  {co.referenceNumber || (
                    <span className="text-gray-600">—</span>
                  )}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-200">
                  {co.title}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-200">
                  {co.changeOrderNumber}
                </td>
                <>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-200">
                    <div className="flex flex-col">
                      {/* Main total - this is base + all amendments */}
                      <span className="font-semibold">
                        $
                        {formatNumber(
                          co._calculated?.budgetCost || co.totalBudgetCost || 0,
                        )}
                      </span>

                      {/* T&M indicator */}
                      {co.isTandMBased && (
                        <span className="text-xs text-teal-400">
                          T&M #
                          {co.linkedTandMId?.timeAndMaterialNumber || "linked"}
                          {tandmCostMap[
                            co.linkedTandMId?._id?.toString() ||
                              co.linkedTandMId?.toString()
                          ]
                            ? ""
                            : " (loading...)"}
                        </span>
                      )}

                      {/* Amendment breakdown - show for ANY CO with amendments */}
                      {(co._calculated?.amendmentCost || 0) !== 0 && (
                        <span className="text-xs text-gray-400 mt-0.5">
                          Base: $
                          {formatNumber(co._calculated?.baseBudgetCost || 0)}
                          <br />
                          <span
                            className={
                              co._calculated.amendmentCost >= 0
                                ? "text-green-400"
                                : "text-red-400"
                            }
                          >
                            Amendments:{" "}
                            {co._calculated.amendmentCost >= 0 ? "+" : ""}$
                            {formatNumber(
                              Math.abs(co._calculated.amendmentCost),
                            )}
                          </span>
                        </span>
                      )}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-200">
                    $
                    {formatNumber(
                      co._calculated?.totalCustomerPrice ||
                        co.totalCustomerPrice ||
                        0,
                    )}
                  </td>
                </>

                <td className="px-6 py-4 whitespace-nowrap text-sm">
                  <span
                    className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                      co.approvalStatus.toLowerCase() === "pending"
                        ? "bg-yellow-600/20 text-yellow-400"
                        : co.approvalStatus.toLowerCase() === "approved"
                          ? "bg-green-600/20 text-green-400"
                          : "bg-red-600/20 text-red-400"
                    }`}
                  >
                    {co.approvalStatus.charAt(0).toUpperCase() +
                      co.approvalStatus.slice(1)}
                  </span>
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-200">
                  {formatDate(co.createdAt)}
                </td>
              </tr>
            ))}
          </tbody>
          {filteredChangeOrders.length > 0 && (
            // REPLACE WITH:
            // REPLACE tfoot with:
            <tfoot className="bg-gray-800">
              <tr>
                <td
                  colSpan="3"
                  className="px-6 py-3 text-left text-sm text-gray-200 font-semibold"
                >
                  Subtotals
                </td>
                <td className="px-6 py-3 text-left text-sm font-semibold">
                  <div className="flex flex-col gap-0.5">
                    {subtotal2.tandmCost > 0 && (
                      <span className="text-teal-400 text-xs">
                        T&amp;M: ${formatNumber(subtotal2.tandmCost)}
                      </span>
                    )}
                    {subtotal2.totalCost > 0 && (
                      <span className="text-gray-200 text-xs">
                        CO: ${formatNumber(subtotal2.totalCost)}
                      </span>
                    )}
                    <span className="text-white font-semibold border-t border-gray-600 pt-0.5">
                      ${formatNumber(subtotal2.totalCost + subtotal2.tandmCost)}
                    </span>
                  </div>
                </td>
                <td className="px-6 py-3 text-left text-sm text-gray-200 font-semibold">
                  ${formatNumber(subtotal2.totalPrice)}
                </td>
                <td colSpan="4" className="px-6 py-3"></td>
              </tr>
            </tfoot>
          )}
        </table>
      </div>

      {filteredChangeOrders.length === 0 || statusFilter !== "all" ? (
        <div className="mt-4 flex justify-between items-center">
          {filteredChangeOrders.length === 0 && (
            <p className="text-gray-400">
              No {activeTab === "billable" ? "billable" : "non-billable"} change
              orders for selected status.
            </p>
          )}
          {statusFilter !== "all" && (
            <button
              onClick={handleClearFilter}
              className="ml-auto px-4 py-2 text-sm text-white font-semibold bg-orange-400 rounded-md hover:bg-orange-500 transition-colors duration-200"
            >
              Clear Filter
            </button>
          )}
        </div>
      ) : null}
    </div>
  );
}
